<script>
	import { tick } from 'svelte';
	import { browser } from '$app/environment';
	import Stack from '$lib/Stack';
	import Text from '$lib/Text';
	import styles from './TOC.module.css';
	import TOCItem from './TOCItem.svelte';

	/** @type {Element | undefined} */
	export let content = undefined;

	/** @typedef {{ id: string, depth: number, heading: string, children: Section[] }} Section */
	/** @type {Section[]} */
	let sections = [];

	/**
	 * @param {Element} heading
	 * @returns {number | null}
	 */
	function getHeadingLevel(heading) {
		const ariaLevelValue = heading.getAttribute('aria-level');
		if (ariaLevelValue) {
			const ariaLevel = parseInt(ariaLevelValue, 10);
			if (!Number.isNaN(ariaLevel)) {
				return ariaLevel;
			}
		}

		const levelValue = heading.tagName.slice(1);
		const level = parseInt(levelValue, 10);
		if (!Number.isNaN(level)) {
			return level;
		}

		return null;
	}

	function updateSections() {
		pending = false;
		if (!content) {
			return;
		}

		/** @type {Section} */
		const root = {
			id: '',
			depth: 0,
			heading: 'root',
			children: [],
		};
		const stack = [root];
		const headingEls = content.querySelectorAll('h1,h2,h3,h4,h5,h6,[role="heading"]');
		for (let i = 0; i < headingEls.length; i++) {
			const heading = headingEls[i];
			const level = getHeadingLevel(heading);
			if (level === null || level === 1 || level > 3) {
				continue;
			}

			const sectionEl = content.querySelector(`[aria-labelledby="${heading.id}"]`);
			const section = {
				id: sectionEl?.id ?? heading.id,
				depth: level - 1,
				heading: /** @type {string} */ (heading.textContent),
				children: [],
			};

			if (stack.length === 0) {
				console.error('[TOC] error creating table of contents: stack.length === 0');
				return;
			}

			while (section.depth <= (stack.at(-1)?.depth ?? 0)) {
				stack.pop();
			}
			stack.at(-1)?.children.push(section);
			stack.push(section);
		}
		sections = root.children;
	}

	let pending = false;
	function scheduleUpdate() {
		if (pending) {
			return;
		}
		queueMicrotask(updateSections);
		pending = true;
	}

	const contentObserver = browser ? new MutationObserver(scheduleUpdate) : null;

	/** @param {Element | undefined} el */
	function observeContent(el) {
		if (!contentObserver) {
			return;
		}
		contentObserver.disconnect();
		if (el) {
			contentObserver.observe(el, { childList: true });
			scheduleUpdate();
		}
	}
	$: observeContent(content);

	let active = new Set();
	const scrollObserver = browser
		? new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							active.add(entry.target.id);
							/** @type {Element | null} */
							let ancestor = entry.target;
							while ((ancestor = ancestor.parentElement)) {
								if (ancestor.id) {
									active.delete(ancestor.id);
								}
							}
						} else {
							active.delete(entry.target.id);
						}
					}
					active = active;
				},
				{ rootMargin: '-198px 0px -128px 0px' },
			)
		: null;

	/** @param {Section} section */
	function observeSection(section) {
		const el = document.getElementById(section.id);
		if (el) {
			/**@type {IntersectionObserver}*/ (scrollObserver).observe(el);
			section.children.forEach(observeSection);
		}
	}

	/** @param {Section[]} sections */
	async function sectionsDidUpdate(sections) {
		if (!scrollObserver) {
			return;
		}
		scrollObserver.disconnect();
		await tick();
		sections.forEach(observeSection);
	}
	$: sectionsDidUpdate(sections);
</script>

{#if sections.length > 1}
	<Stack as="nav" class={styles.nav} gap="sm" valign="top">
		<!-- <Heading level={3}>Contents</Heading> -->
		<Text size="sm" tone="subtle" weight="semibold">Contents</Text>
		<Stack as="ol" class={styles.section} style="min-height: unset;">
			{#each sections as section (section.id)}
				<TOCItem {active} {...section} />
			{/each}
		</Stack>
	</Stack>
{/if}
<!-- <div class={styles.spacer}></div> -->
